import { computePeaks } from '../audio/analysis';
import { audioCtx } from '../audio/context';
import { getSample, putSample } from '../audio/registry';
import type { PlayMode } from '../audio/sequencer';
import { encodeWav } from '../audio/wav';
import type { Project } from '../types';
import type { View } from './useStore';

/**
 * Session persistence — what you get back when you refresh.
 *
 * IndexedDB rather than localStorage because the audio has to come with it, and
 * a couple of loops blows the 5 MB localStorage budget immediately. Audio is
 * stored as 16-bit WAV bytes: structured-cloneable, no base64 inflation, and
 * decodable straight back through the same path an import uses.
 *
 * Distinct from `persist.ts`, which is the explicit "save this project to a
 * file I can hand to someone" flow. This one is invisible and automatic.
 */

const DB_NAME = 'happychopper';
const STORE = 'session';
const KEY = 'current';
const RECORD_VERSION = 1;

export interface SessionUi {
  view: View;
  selectedTrackId: string | null;
  selectedSourceId: string | null;
  playMode: PlayMode;
}

interface SessionRecord {
  version: number;
  savedAt: number;
  project: Project;
  /** Source id -> WAV bytes. */
  audio: Record<string, ArrayBuffer>;
  ui: SessionUi;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB unavailable'));
  });
}

/**
 * Encoded audio, cached by source id. A source's samples never change after
 * import — only its metadata does — so re-encoding on every autosave would be
 * pure waste.
 */
const encoded = new Map<string, ArrayBuffer>();

async function bytesFor(sourceId: string): Promise<ArrayBuffer | null> {
  const cached = encoded.get(sourceId);
  if (cached) return cached;
  const entry = getSample(sourceId);
  if (!entry) return null;
  const bytes = await encodeWav(entry.buffer, 16).arrayBuffer();
  encoded.set(sourceId, bytes);
  return bytes;
}

export async function saveSession(project: Project, ui: SessionUi): Promise<void> {
  const audio: Record<string, ArrayBuffer> = {};
  for (const src of project.sources) {
    const bytes = await bytesFor(src.id);
    if (bytes) audio[src.id] = bytes;
  }
  // Drop encodings for sources that have been deleted.
  for (const id of encoded.keys()) {
    if (!project.sources.some((s) => s.id === id)) encoded.delete(id);
  }

  const record: SessionRecord = {
    version: RECORD_VERSION,
    savedAt: Date.now(),
    project,
    audio,
    ui,
  };

  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(record, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Session save failed'));
    tx.onabort = () => reject(tx.error ?? new Error('Session save aborted'));
  });
  db.close();
}

export interface RestoredSession {
  project: Project;
  ui: SessionUi;
  /** Sources whose audio couldn't be decoded and were dropped. */
  lost: string[];
}

/**
 * Reads the last session back and re-registers its audio.
 *
 * A source whose audio fails to decode is removed rather than left in place —
 * a loop that's in the library but silent, and a track pointing at it, is a
 * more confusing state than one that's simply gone.
 */
export async function loadSession(): Promise<RestoredSession | null> {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return null;
  }

  const record = await new Promise<SessionRecord | null>((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve((req.result as SessionRecord) ?? null);
    req.onerror = () => resolve(null);
  });
  db.close();

  if (!record || record.version !== RECORD_VERSION || !record.project) return null;

  const ctx = audioCtx();
  const lost: string[] = [];
  for (const src of record.project.sources) {
    const bytes = record.audio?.[src.id];
    if (!bytes) {
      lost.push(src.name);
      continue;
    }
    try {
      // decodeAudioData detaches its input, so hand it a copy — the record may
      // be read again if the restore is retried.
      const buffer = await ctx.decodeAudioData(bytes.slice(0));
      putSample(src.id, { buffer, peaks: computePeaks(buffer) });
      encoded.set(src.id, bytes);
    } catch {
      lost.push(src.name);
    }
  }

  if (lost.length) {
    const gone = new Set(
      record.project.sources.filter((s) => !getSample(s.id)).map((s) => s.id),
    );
    record.project.sources = record.project.sources.filter((s) => !gone.has(s.id));
    for (const t of record.project.tracks) {
      if (t.sourceId && gone.has(t.sourceId)) t.sourceId = null;
    }
  }

  return { project: record.project, ui: record.ui, lost };
}

export async function clearSession(): Promise<void> {
  encoded.clear();
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
    db.close();
  } catch {
    /* nothing to clear */
  }
}
